import type { LucideIcon } from "lucide-react";
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  ClipboardCheck,
  CloudRain,
  Home,
  Layers3,
  MapPinned,
  Phone,
  ShieldCheck,
} from "lucide-react";

import { QuoteForm } from "@/components/site/QuoteForm";
import { EmergencyIntakeForm } from "@/components/site/EmergencyIntakeForm";
import { PlannedEstimateForm } from "@/components/site/PlannedEstimateForm";
import { Button } from "@/components/ui/button";
import { bcCoverageRegions } from "@/config/bc-coverage";
import { business } from "@/config/business";
import { confirmedServiceAreas } from "@/config/service-areas";
import { serviceBySlug } from "@/config/services";
import type { LocaleCode } from "@/i18n";
import { localizeServicePage, type PageUi } from "@/i18n/content";
import { trackAnalyticsEvent } from "@/lib/analytics";

export type ExpansionPageKind =
  | "emergency-roofing"
  | "commercial-roofing"
  | "strata-roofing"
  | "residential-roofing"
  | "roof-systems"
  | "roof-estimate"
  | "british-columbia";

type PathBuilder = (href: string) => string;

type ExpansionPageProps = {
  kind: ExpansionPageKind;
  path: PathBuilder;
  locale: LocaleCode;
  ui?: PageUi | undefined;
};

type PageCopy = {
  eyebrow: string;
  title: string;
  body: string;
  sectionEyebrow: string;
  sectionTitle: string;
  sectionBody: string;
  focus: readonly [string, string, string];
  detailParagraphs?: readonly string[];
};

type BasePageConfig = {
  image: string;
  imageAlt: string;
  serviceSlugs: readonly string[];
  icon: LucideIcon;
};

const basePages: Record<ExpansionPageKind, BasePageConfig> = {
  "emergency-roofing": {
    image: "/media/expansion-pages/emergency-roofing-lower-mainland.jpg",
    imageAlt: "Aspirational luxury home protected during heavy Lower Mainland rain",
    serviceSlugs: [
      "emergency-roof-leak-repair",
      "storm-tarping-emergency-dry-in",
      "shingle-spot-repair",
      "flashing-roof-penetration-repair",
      "skylight-leak-flashing-repair",
      "chimney-flashing-repair",
      "flat-low-slope-roof-repair",
      "metal-roof-repair-maintenance",
    ],
    icon: CloudRain,
  },
  "commercial-roofing": {
    image: "/media/expansion-pages/commercial-roofing-bc.jpg",
    imageAlt: "Aspirational premium commercial property with a carefully detailed roof",
    serviceSlugs: [
      "flat-low-slope-roof-repair",
      "flat-roof-replacement-sbs-tpo-pvc",
      "roof-health-check-maintenance",
      "metal-roof-repair-maintenance",
      "gutter-replacement",
    ],
    icon: Building2,
  },
  "strata-roofing": {
    image: "/media/expansion-pages/strata-roofing-bc.jpg",
    imageAlt: "Aspirational luxury strata community with a coordinated roofing design",
    serviceSlugs: [
      "roof-health-check-maintenance",
      "emergency-roof-leak-repair",
      "asphalt-shingle-replacement",
      "flat-roof-replacement-sbs-tpo-pvc",
      "moss-removal-roof-gutter",
      "fascia-soffit-repair-replacement",
    ],
    icon: ShieldCheck,
  },
  "residential-roofing": {
    image: "/media/expansion-pages/residential-roofing-bc.jpg",
    imageAlt: "Aspirational British Columbia residence with premium roofing",
    serviceSlugs: [
      "asphalt-shingle-replacement",
      "cedar-shake-roof-replacement",
      "metal-roof-replacement",
      "roof-health-check-maintenance",
      "gutter-replacement",
      "roof-ventilation-upgrade",
      "moss-removal-roof-gutter",
      "cedar-shake-repair-restoration",
      "gutter-cleaning-downspout-flush",
    ],
    icon: Home,
  },
  "roof-systems": {
    image: "/media/expansion-pages/roof-systems-bc.jpg",
    imageAlt: "Aspirational luxury architecture displaying several premium roof systems",
    serviceSlugs: [
      "asphalt-shingle-replacement",
      "cedar-shake-roof-replacement",
      "metal-roof-replacement",
      "flat-roof-replacement-sbs-tpo-pvc",
    ],
    icon: Layers3,
  },
  "roof-estimate": {
    image: "/media/expansion-pages/roof-estimate-bc.jpg",
    imageAlt: "Aspirational luxury home being considered for a future roofing project",
    serviceSlugs: [
      "roof-health-check-maintenance",
      "emergency-roof-leak-repair",
      "asphalt-shingle-replacement",
      "flat-roof-replacement-sbs-tpo-pvc",
    ],
    icon: ClipboardCheck,
  },
  "british-columbia": {
    image: "/media/expansion-pages/british-columbia-roofing.jpg",
    imageAlt: "Aspirational British Columbia property with mountain-ready premium roofing",
    serviceSlugs: [
      "asphalt-shingle-replacement",
      "metal-roof-replacement",
      "flat-roof-replacement-sbs-tpo-pvc",
      "roof-health-check-maintenance",
    ],
    icon: MapPinned,
  },
};

const englishCopy: Record<ExpansionPageKind, PageCopy> = {
  "emergency-roofing": {
    eyebrow: "Urgent roofing · Lower Mainland",
    title: "Contain the leak before the damage travels",
    body: "Call for urgent roof-leak assessment across the Lower Mainland. Response depends on crew availability, safe access and practical dispatch conditions.",
    sectionEyebrow: "Direct the right response",
    sectionTitle: "Start with the part of the roof that failed",
    sectionBody:
      "These established Revolin services remain the canonical paths for leak investigation, emergency dry-in and system-specific repair.",
    focus: ["Active water entry", "Storm and wind damage", "Flashings, skylights and penetrations"],
  },
  "commercial-roofing": {
    eyebrow: "Commercial roofing · British Columbia",
    title: "Roofing decisions built around the asset",
    body: "Assessment-led repair, replacement and maintenance pathways for occupied commercial buildings. Scheduled work outside the Lower Mainland depends on scope, access, mobilization and scheduling.",
    sectionEyebrow: "Existing technical services",
    sectionTitle: "Proven service pages, connected for commercial intent",
    sectionBody:
      "Commercial projects link into Revolin’s established low-slope, replacement and maintenance services rather than duplicating their technical guidance.",
    focus: [
      "Drainage and membrane condition",
      "Occupied-site coordination",
      "Lifecycle and maintenance planning",
    ],
  },
  "strata-roofing": {
    eyebrow: "Strata and multi-family",
    title: "A clearer roofing path for councils and managers",
    body: "Condition-led roofing support for strata and multi-family properties, with attention to resident access, shared decisions and staged project planning.",
    sectionEyebrow: "Connected scopes",
    sectionTitle: "One building strategy, supported by specialist services",
    sectionBody:
      "Use the existing service pages to review likely repair, maintenance and replacement pathways before requesting a project-specific assessment.",
    focus: ["Council-ready findings", "Resident and access planning", "Capital-project sequencing"],
  },
  "residential-roofing": {
    eyebrow: "Residential roofing · Metro Vancouver",
    title: "Build the future of your home from the roof down",
    body: "Repair, maintenance and replacement planning for Metro Vancouver and Lower Mainland homes — shaped by the existing roof assembly, architecture and local rain exposure, not a one-size product pitch.",
    sectionEyebrow: "Residential services",
    sectionTitle: "Choose a need - not a predetermined product",
    sectionBody:
      "Explore the published residential services, then request a roof assessment. Revolin Roofing documents what we can observe and returns a written scope before recommending asphalt, cedar, metal or low-slope work.",
    focus: ["Weather protection", "Architectural fit", "Ventilation and water management"],
    detailParagraphs: [
      "Revolin Roofing serves homeowners across Metro Vancouver and the Lower Mainland from a Burnaby-based team. We are a Revolin Group company; roofing stays our focus while the parent group handles broader renovations and custom homes when owners need that path.",
      "Residential work begins with assessment: we review the covering, flashings, penetrations, ventilation and drainage, note interior evidence where relevant, and explain findings in plain language. Quotes for re-roofing require seeing the roof — we do not price a full replacement from photos alone.",
      "Steep-slope homes commonly move through asphalt shingle repair or replacement, cedar shake repair or replacement, or standing-seam metal when longevity and design matter. Low-slope sections need membrane assemblies with careful slope-to-drain and transitions at walls, curbs and parapets.",
      "Maintenance matters in the Pacific Northwest. Moss removal, gutter cleaning, ventilation upgrades and health checks help catch drainage and detail problems before they become interior damage. Emergency leak repair is available throughout the Lower Mainland subject to crew availability and safe access.",
      "Every completed project includes a written Revolin workmanship warranty and the manufacturer material warranty that applies to the system installed. Summaries appear on the About page; the controlling terms are always the documents issued with your project.",
      "Use the service links on this page for technical scope on each offering, or request a roof assessment when you are ready to confirm conditions on your home.",
    ],
  },
  "roof-systems": {
    eyebrow: "Roof systems",
    title: "The right roof is a complete assembly",
    body: "Compare established shingle, cedar, metal and low-slope service pathways. System selection follows geometry, substrate, drainage, exposure and design intent.",
    sectionEyebrow: "Canonical system pages",
    sectionTitle: "Four distinct systems. No universal answer.",
    sectionBody:
      "Each system page explains where the material fits and what must be assessed before a repair or replacement scope is recommended.",
    focus: ["Steep-slope coverings", "Low-slope membranes", "Edges, drainage and penetrations"],
  },
  "roof-estimate": {
    eyebrow: "Roof project intake",
    title: "A useful estimate begins with useful information",
    body: "Tell us about the property, roof and timing. Revolin will review the information and confirm the next step; this page does not generate an unverified instant price.",
    sectionEyebrow: "Before you submit",
    sectionTitle: "What helps us understand the project",
    sectionBody:
      "Share what you know. Exact measurements and a material choice are not required to begin, and no public price range is invented from incomplete details.",
    focus: [
      "Property location and type",
      "Known issues and current roof",
      "Timing, access and helpful photos",
    ],
  },
  "british-columbia": {
    eyebrow: "Scheduled roofing · British Columbia",
    title: "Roofing projects planned for BC conditions",
    body: "Urgent response is focused on the Lower Mainland. Scheduled residential, commercial, strata and specialty projects may be considered throughout British Columbia.",
    sectionEyebrow: "Coverage model",
    sectionTitle: "Local urgent response. Province-wide project planning.",
    sectionBody:
      "Availability outside the Lower Mainland depends on project scope, access, mobilization, logistics and scheduling. A listed region is a service area - not a claim of a local office.",
    focus: [
      "Lower Mainland urgent response",
      "Scheduled work across BC",
      "Scope-based mobilization review",
    ],
  },
};

const persianCopy: Record<ExpansionPageKind, PageCopy> = {
  "emergency-roofing": {
    eyebrow: "خدمات فوری سقف · لوئر مین‌لند",
    title: "پیش از گسترش خسارت، جلوی نفوذ آب را بگیرید",
    body: "برای بررسی فوری نشتی سقف در لوئر مین‌لند تماس بگیرید. امکان اعزام به ظرفیت تیم، دسترسی ایمن و شرایط عملیاتی بستگی دارد.",
    sectionEyebrow: "هدایت درخواست به خدمت مناسب",
    sectionTitle: "از همان بخشی شروع کنید که دچار مشکل شده است",
    sectionBody:
      "این خدمات اصلی رولین، مسیرهای تخصصی بررسی نشتی، آب‌بندی موقت اضطراری و تعمیر هر نوع سیستم سقف هستند.",
    focus: ["نفوذ فعال آب", "خسارت باد و طوفان", "فلاشینگ، نورگیر و محل عبور تأسیسات"],
  },
  "commercial-roofing": {
    eyebrow: "سقف تجاری · بریتیش کلمبیا",
    title: "تصمیم‌های فنی متناسب با ارزش و کاربری ساختمان",
    body: "مسیرهای کارشناسی‌شده برای تعمیر، تعویض و نگهداری سقف ساختمان‌های تجاریِ در حال بهره‌برداری. اجرای پروژه خارج از لوئر مین‌لند به دامنه کار، دسترسی، تجهیز کارگاه و زمان‌بندی بستگی دارد.",
    sectionEyebrow: "خدمات فنی موجود",
    sectionTitle: "خدمات تخصصی رولین برای نیازهای ساختمان تجاری",
    sectionBody:
      "پروژه‌های تجاری به صفحات اصلی خدمات سقف کم‌شیب، تعویض و نگهداری متصل می‌شوند تا اطلاعات فنی تکراری ایجاد نشود.",
    focus: ["زهکشی و وضعیت غشا", "هماهنگی در ساختمان فعال", "برنامه نگهداری و عمر مفید"],
  },
  "strata-roofing": {
    eyebrow: "استراتا و ساختمان‌های چندواحدی",
    title: "مسیر روشن‌تر برای شوراها و مدیران ساختمان",
    body: "خدمات سقف مبتنی بر بررسی وضعیت، با توجه به رفت‌وآمد ساکنان، تصمیم‌گیری مشترک و برنامه‌ریزی مرحله‌ای پروژه.",
    sectionEyebrow: "دامنه‌های مرتبط",
    sectionTitle: "یک راهبرد یکپارچه، با پشتیبانی خدمات تخصصی",
    sectionBody:
      "پیش از درخواست ارزیابی اختصاصی، مسیرهای احتمالی تعمیر، نگهداری و تعویض را در صفحات خدمات موجود بررسی کنید.",
    focus: [
      "گزارش مناسب ارائه به شورا",
      "برنامه‌ریزی دسترسی و ساکنان",
      "مرحله‌بندی پروژه سرمایه‌ای",
    ],
  },
  "residential-roofing": {
    eyebrow: "سقف خانه‌های مسکونی",
    title: "آینده خانه‌تان را از سقف آن بسازید",
    body: "برنامه‌ریزی تعمیر، نگهداری و تعویض باکیفیت، متناسب با معماری خانه، ساختار فعلی سقف و شرایط آب‌وهوایی بریتیش کلمبیا.",
    sectionEyebrow: "خدمات مسکونی",
    sectionTitle: "ابتدا نیاز ساختمان را مشخص کنید، نه محصول را",
    sectionBody:
      "خدمات مسکونی را بررسی کنید و سپس برای مشخص‌شدن سیستم و دامنه کار مناسب، درخواست ارزیابی بدهید.",
    focus: ["محافظت در برابر آب‌وهوا", "هماهنگی با معماری", "تهویه و مدیریت آب"],
  },
  "roof-systems": {
    eyebrow: "سیستم‌های سقف",
    title: "سقف مناسب، یک مجموعه کامل و هماهنگ است",
    body: "مسیرهای تخصصی شینگل، چوب سدر، فلز و سقف کم‌شیب را مقایسه کنید. انتخاب سیستم به فرم سقف، زیرسازی، زهکشی، شرایط محیطی و هدف طراحی بستگی دارد.",
    sectionEyebrow: "صفحات اصلی سیستم‌ها",
    sectionTitle: "چهار سیستم متفاوت؛ بدون یک پاسخ یکسان برای همه",
    sectionBody:
      "در هر صفحه می‌بینید آن سیستم برای چه شرایطی مناسب است و پیش از پیشنهاد تعمیر یا تعویض چه مواردی باید بررسی شود.",
    focus: ["پوشش سقف‌های شیب‌دار", "غشای سقف‌های کم‌شیب", "لبه‌ها، زهکشی و محل عبور تأسیسات"],
  },
  "roof-estimate": {
    eyebrow: "ثبت اطلاعات پروژه سقف",
    title: "برآورد مفید با اطلاعات دقیق شروع می‌شود",
    body: "مشخصات ملک، سقف و زمان موردنظر را برای ما بفرستید. رولین اطلاعات را بررسی و مرحله بعد را اعلام می‌کند؛ این صفحه قیمت فوری و تأییدنشده تولید نمی‌کند.",
    sectionEyebrow: "پیش از ارسال",
    sectionTitle: "چه اطلاعاتی به شناخت پروژه کمک می‌کند؟",
    sectionBody:
      "هرچه می‌دانید ثبت کنید. برای شروع نیازی به اندازه‌گیری دقیق یا انتخاب قطعی مصالح نیست و بر پایه اطلاعات ناقص، بازه قیمت ساختگی نمایش داده نمی‌شود.",
    focus: ["موقعیت و نوع ملک", "مشکلات شناخته‌شده و سقف فعلی", "زمان‌بندی، دسترسی و عکس‌های مفید"],
  },
  "british-columbia": {
    eyebrow: "پروژه‌های برنامه‌ریزی‌شده · بریتیش کلمبیا",
    title: "پروژه‌های سقف متناسب با شرایط بریتیش کلمبیا",
    body: "پاسخ‌گویی فوری بر لوئر مین‌لند متمرکز است. پروژه‌های برنامه‌ریزی‌شده مسکونی، تجاری، استراتا و تخصصی در سراسر بریتیش کلمبیا قابل بررسی هستند.",
    sectionEyebrow: "مدل پوشش خدمات",
    sectionTitle: "پاسخ فوری محلی؛ برنامه‌ریزی پروژه در سراسر استان",
    sectionBody:
      "پذیرش کار خارج از لوئر مین‌لند به دامنه پروژه، دسترسی، تجهیز، تدارکات و زمان‌بندی بستگی دارد. نام یک منطقه به‌معنای وجود دفتر محلی رولین در آنجا نیست.",
    focus: [
      "پاسخ فوری در لوئر مین‌لند",
      "پروژه‌های برنامه‌ریزی‌شده در سراسر استان",
      "بررسی شرایط تجهیز و اعزام",
    ],
  },
};

const arabicCopy: Record<ExpansionPageKind, PageCopy> = {
  "emergency-roofing": {
    eyebrow: "خدمات أسقف عاجلة · لوَر مينلاند",
    title: "أوقف تسرّب المياه قبل امتداد الضرر",
    body: "اتصل لطلب تقييم عاجل لتسرّب السقف في منطقة لوَر مينلاند. تعتمد إمكانية الاستجابة على توافر الطاقم وسلامة الوصول وظروف الإرسال العملية.",
    sectionEyebrow: "توجيه الطلب إلى الخدمة المناسبة",
    sectionTitle: "ابدأ بالجزء الذي حدث فيه الخلل",
    sectionBody:
      "تبقى خدمات ريفولين الأساسية هي المسارات المتخصصة لفحص التسرّب والحماية المؤقتة والإصلاح المناسب لنظام السقف.",
    focus: [
      "دخول المياه إلى المبنى",
      "أضرار الرياح والعواصف",
      "الصفائح الانتقالية والمناور والاختراقات",
    ],
  },
  "commercial-roofing": {
    eyebrow: "أسقف تجارية · كولومبيا البريطانية",
    title: "قرارات تسقيف تراعي المبنى كأصل",
    body: "مسارات قائمة على التقييم لإصلاح أسقف المباني التجارية المشغولة واستبدالها وصيانتها. يعتمد العمل المجدول خارج لوَر مينلاند على نطاق المشروع والوصول والتجهيز والجدول الزمني.",
    sectionEyebrow: "الخدمات الفنية القائمة",
    sectionTitle: "خدمات مثبتة مترابطة لتلبية الاحتياجات التجارية",
    sectionBody:
      "ترتبط المشاريع التجارية بصفحات ريفولين الأساسية للأسقف منخفضة الميل والاستبدال والصيانة بدلاً من تكرار الإرشادات الفنية.",
    focus: ["التصريف وحالة الغشاء", "التنسيق في المباني المشغولة", "تخطيط الصيانة والعمر التشغيلي"],
  },
  "strata-roofing": {
    eyebrow: "الستراتا والمباني متعددة الوحدات",
    title: "مسار أوضح للمجالس ومديري العقارات",
    body: "دعم تسقيف مبني على حالة السقف، مع مراعاة وصول السكان والقرارات المشتركة والتخطيط المرحلي للمشروع.",
    sectionEyebrow: "نطاقات مترابطة",
    sectionTitle: "استراتيجية موحدة تدعمها خدمات متخصصة",
    sectionBody:
      "راجع مسارات الإصلاح والصيانة والاستبدال المحتملة في صفحات الخدمات القائمة قبل طلب تقييم خاص بالمشروع.",
    focus: ["نتائج مناسبة لمجلس الستراتا", "تخطيط وصول السكان", "مراحل المشروع الرأسمالي"],
  },
  "residential-roofing": {
    eyebrow: "أسقف المنازل",
    title: "ابنِ مستقبل منزلك ابتداءً من السقف",
    body: "تخطيط للإصلاح والصيانة والاستبدال المتميز بما يناسب المنزل وتركيب السقف الحالي وظروف التعرض المتنوعة في كولومبيا البريطانية.",
    sectionEyebrow: "الخدمات السكنية",
    sectionTitle: "اختر الحاجة أولاً، لا منتجاً محدداً مسبقاً",
    sectionBody:
      "استكشف الخدمات السكنية القائمة، ثم اطلب تقييماً لتحديد النظام ونطاق العمل الملائمين للحالة الفعلية.",
    focus: ["الحماية من الطقس", "التوافق المعماري", "التهوية وإدارة المياه"],
  },
  "roof-systems": {
    eyebrow: "أنظمة الأسقف",
    title: "السقف المناسب منظومة متكاملة",
    body: "قارن مسارات القوباء الإسفلتية وخشب الأرز والمعدن والأسقف منخفضة الميل. يعتمد اختيار النظام على الشكل والسطح السفلي والتصريف والتعرض والهدف التصميمي.",
    sectionEyebrow: "صفحات الأنظمة الأساسية",
    sectionTitle: "أربعة أنظمة مختلفة، ولا حل واحد يناسب الجميع",
    sectionBody:
      "توضح كل صفحة أين يناسب النظام وما الذي يجب تقييمه قبل التوصية بالإصلاح أو الاستبدال.",
    focus: ["أغطية الأسقف المائلة", "أغشية الأسقف منخفضة الميل", "الحواف والتصريف والاختراقات"],
  },
  "roof-estimate": {
    eyebrow: "بيانات مشروع السقف",
    title: "التقدير المفيد يبدأ بمعلومات مفيدة",
    body: "أخبرنا عن العقار والسقف والتوقيت. تراجع ريفولين المعلومات وتؤكد الخطوة التالية؛ ولا تنشئ هذه الصفحة سعراً فورياً غير موثّق.",
    sectionEyebrow: "قبل الإرسال",
    sectionTitle: "ما الذي يساعدنا على فهم المشروع؟",
    sectionBody:
      "أرسل ما تعرفه. لا يلزم توفر قياسات دقيقة أو اختيار نهائي للمادة للبدء، ولن نعرض نطاقاً سعرياً مختلقاً اعتماداً على معلومات ناقصة.",
    focus: [
      "موقع العقار ونوعه",
      "المشكلات المعروفة والسقف الحالي",
      "التوقيت والوصول والصور المفيدة",
    ],
  },
  "british-columbia": {
    eyebrow: "مشاريع مجدولة · كولومبيا البريطانية",
    title: "مشاريع أسقف مخططة لظروف كولومبيا البريطانية",
    body: "تتركز الاستجابة العاجلة في لوَر مينلاند، ويمكن بحث المشاريع السكنية والتجارية ومشاريع الستراتا والتخصصية المجدولة في أنحاء كولومبيا البريطانية.",
    sectionEyebrow: "نموذج نطاق الخدمة",
    sectionTitle: "استجابة عاجلة محلية وتخطيط مشاريع على مستوى المقاطعة",
    sectionBody:
      "يعتمد توفر العمل خارج لوَر مينلاند على نطاق المشروع والوصول والتجهيز والخدمات اللوجستية والجدول الزمني. إدراج منطقة يعني أنها نطاق خدمة، لا وجود مكتب محلي فيها.",
    focus: [
      "استجابة عاجلة في لوَر مينلاند",
      "عمل مجدول في أنحاء المقاطعة",
      "مراجعة متطلبات التجهيز وفق النطاق",
    ],
  },
};

const simplifiedChineseCopy: Record<ExpansionPageKind, PageCopy> = {
  "emergency-roofing": {
    eyebrow: "紧急屋顶服务 · 低陆平原",
    title: "先控制漏水，再阻止损害扩大",
    body: "低陆平原地区可致电申请紧急屋顶漏水检查。实际响应取决于施工团队档期、现场能否安全进入以及调度条件。",
    sectionEyebrow: "找到合适的处理方式",
    sectionTitle: "从真正发生故障的屋顶部位查起",
    sectionBody:
      "以下是 Revolin 已正式提供的服务，涵盖漏水排查、紧急临时防水和针对不同屋顶系统的维修。",
    focus: ["正在发生的进水", "风暴及强风损坏", "泛水、天窗与屋面穿透处"],
  },
  "commercial-roofing": {
    eyebrow: "商业屋顶 · 不列颠哥伦比亚省",
    title: "围绕物业长期价值做屋顶决策",
    body: "为仍在运营的商业建筑提供以检查为基础的维修、更换和维护方案。低陆平原以外的预约项目，需根据工程范围、现场通行、进场组织和档期确认。",
    sectionEyebrow: "现有专业服务",
    sectionTitle: "将成熟服务组合成清晰的商业屋顶路径",
    sectionBody: "商业项目会衔接至 Revolin 现有的低坡屋顶、更换和维护服务页面，避免重复技术说明。",
    focus: ["排水与防水层状况", "营业期间的现场协调", "使用寿命与维护规划"],
  },
  "strata-roofing": {
    eyebrow: "共管物业与多户住宅",
    title: "为物业委员会和管理人员提供更清晰的屋顶路径",
    body: "根据屋顶实际状况，为共管物业及多户住宅提供支持，并兼顾住户出入、共同决策和分阶段工程安排。",
    sectionEyebrow: "相互衔接的工程范围",
    sectionTitle: "以一套建筑策略统筹各项专业服务",
    sectionBody:
      "申请针对项目的现场评估前，可先查看现有服务页面，了解可能适用的维修、维护和更换方案。",
    focus: ["便于委员会审阅的检查结果", "住户通知与出入安排", "资本工程的阶段规划"],
  },
  "residential-roofing": {
    eyebrow: "住宅屋顶",
    title: "从屋顶开始，塑造理想住宅的未来",
    body: "根据住宅建筑、现有屋顶构造以及不列颠哥伦比亚省各地不同的暴露条件，规划维修、维护和高品质更换工程。",
    sectionEyebrow: "住宅服务",
    sectionTitle: "先确定实际需求，而不是预设某种产品",
    sectionBody: "浏览现有住宅服务，再申请现场评估，以确认哪种屋顶系统和工程范围符合实际状况。",
    focus: ["防风雨保护", "与建筑风格协调", "通风与雨水管理"],
  },
  "roof-systems": {
    eyebrow: "屋顶系统",
    title: "合适的屋顶，是一套完整协调的系统",
    body: "比较沥青瓦、雪松、金属和低坡屋顶的现有服务路径。系统选择取决于屋面形状、基层、排水、环境暴露和设计目标。",
    sectionEyebrow: "正式系统页面",
    sectionTitle: "四种不同系统，没有放之四海而皆准的答案",
    sectionBody: "每个页面都会说明该系统适合哪些情况，以及提出维修或更换建议前需要检查哪些条件。",
    focus: ["坡屋面覆盖材料", "低坡屋面防水层", "收边、排水与穿透细部"],
  },
  "roof-estimate": {
    eyebrow: "屋顶项目资料",
    title: "可靠的估算，始于有用的项目信息",
    body: "请告诉我们物业、屋顶现状和期望时间。Revolin 会审核资料并确认下一步；本页面不会根据未经核实的信息即时生成价格。",
    sectionEyebrow: "提交之前",
    sectionTitle: "哪些资料能帮助我们了解项目",
    sectionBody:
      "提供您目前掌握的信息即可。开始咨询无需精确尺寸或预先选定材料，我们也不会根据不完整资料编造价格区间。",
    focus: ["物业位置与类型", "已知问题与现有屋顶", "时间、现场通行及相关照片"],
  },
  "british-columbia": {
    eyebrow: "预约屋顶项目 · 不列颠哥伦比亚省",
    title: "针对不列颠哥伦比亚省环境规划屋顶工程",
    body: "紧急响应以低陆平原为主；不列颠哥伦比亚省其他地区的住宅、商业、共管物业及专业预约项目也可提出咨询。",
    sectionEyebrow: "服务覆盖方式",
    sectionTitle: "本地紧急响应，全省预约项目规划",
    sectionBody:
      "低陆平原以外的项目能否承接，取决于工程范围、现场通行、进场安排、物流与档期。列出某个地区仅表示可评估服务，并不代表当地设有分公司。",
    focus: ["低陆平原紧急响应", "全省预约工程", "按项目范围评估进场条件"],
  },
};

const traditionalChineseCopy: Record<ExpansionPageKind, PageCopy> = {
  "emergency-roofing": {
    eyebrow: "緊急屋頂服務 · 低陸平原",
    title: "先控制漏水，再阻止損害擴大",
    body: "低陸平原地區可致電申請緊急屋頂漏水檢查。實際回應取決於施工團隊檔期、現場能否安全進入及調度條件。",
    sectionEyebrow: "找到合適的處理方式",
    sectionTitle: "從真正發生故障的屋頂位置查起",
    sectionBody:
      "以下是 Revolin 已正式提供的服務，涵蓋漏水排查、緊急臨時防水及針對不同屋頂系統的維修。",
    focus: ["正在發生的進水", "風暴及強風損壞", "泛水、天窗與屋面穿透處"],
  },
  "commercial-roofing": {
    eyebrow: "商業屋頂 · 卑詩省",
    title: "圍繞物業長期價值作出屋頂決策",
    body: "為仍在營運的商業建築提供以檢查為基礎的維修、更換及保養方案。低陸平原以外的預約項目，須按工程範圍、現場通行、進場安排及檔期確認。",
    sectionEyebrow: "現有專業服務",
    sectionTitle: "將成熟服務整合成清晰的商業屋頂路徑",
    sectionBody: "商業項目會連接至 Revolin 現有的低坡屋頂、更換及保養服務頁面，避免重複技術說明。",
    focus: ["排水與防水層狀況", "營運期間的現場協調", "使用壽命與保養規劃"],
  },
  "strata-roofing": {
    eyebrow: "共管物業與多戶住宅",
    title: "為物業委員會及管理人員提供更清晰的屋頂路徑",
    body: "根據屋頂實際狀況，為共管物業及多戶住宅提供支援，並兼顧住戶出入、共同決策及分階段工程安排。",
    sectionEyebrow: "互相銜接的工程範圍",
    sectionTitle: "以一套建築策略統籌各項專業服務",
    sectionBody:
      "申請針對項目的現場評估前，可先查看現有服務頁面，了解可能適用的維修、保養及更換方案。",
    focus: ["便於委員會審閱的檢查結果", "住戶通知與出入安排", "資本工程的階段規劃"],
  },
  "residential-roofing": {
    eyebrow: "住宅屋頂",
    title: "從屋頂開始，塑造理想住宅的未來",
    body: "根據住宅建築、現有屋頂構造及卑詩省各地不同的暴露條件，規劃維修、保養及高品質更換工程。",
    sectionEyebrow: "住宅服務",
    sectionTitle: "先確定實際需要，而非預設某種產品",
    sectionBody: "瀏覽現有住宅服務，再申請現場評估，以確認哪種屋頂系統及工程範圍符合實際狀況。",
    focus: ["防風雨保護", "配合建築風格", "通風與雨水管理"],
  },
  "roof-systems": {
    eyebrow: "屋頂系統",
    title: "合適的屋頂，是一套完整協調的系統",
    body: "比較瀝青瓦、雪松、金屬及低坡屋頂的現有服務路徑。系統選擇取決於屋面形狀、基層、排水、環境暴露及設計目標。",
    sectionEyebrow: "正式系統頁面",
    sectionTitle: "四種不同系統，沒有一個答案適合所有建築",
    sectionBody: "每個頁面均會說明該系統適合哪些情況，以及提出維修或更換建議前需要檢查哪些條件。",
    focus: ["斜屋面覆蓋材料", "低坡屋面防水層", "收邊、排水與穿透細部"],
  },
  "roof-estimate": {
    eyebrow: "屋頂項目資料",
    title: "可靠的估算，始於有用的項目資料",
    body: "請告訴我們物業、屋頂現況及期望時間。Revolin 會審核資料並確認下一步；本頁面不會按未經核實的資料即時產生價格。",
    sectionEyebrow: "提交之前",
    sectionTitle: "哪些資料有助我們了解項目",
    sectionBody:
      "提供您目前掌握的資料即可。開始諮詢無需精確尺寸或預先選定材料，我們亦不會根據不完整資料虛構價格範圍。",
    focus: ["物業位置與類型", "已知問題與現有屋頂", "時間、現場通行及相關照片"],
  },
  "british-columbia": {
    eyebrow: "預約屋頂項目 · 卑詩省",
    title: "針對卑詩省環境規劃屋頂工程",
    body: "緊急回應以低陸平原為主；卑詩省其他地區的住宅、商業、共管物業及專業預約項目亦可提出查詢。",
    sectionEyebrow: "服務覆蓋方式",
    sectionTitle: "本地緊急回應，全省預約項目規劃",
    sectionBody:
      "低陸平原以外的項目能否承接，取決於工程範圍、現場通行、進場安排、物流及檔期。列出某個地區只表示可評估服務，並不代表當地設有分公司。",
    focus: ["低陸平原緊急回應", "全省預約工程", "按項目範圍評估進場條件"],
  },
};

const punjabiCopy: Record<ExpansionPageKind, PageCopy> = {
  "emergency-roofing": {
    eyebrow: "ਐਮਰਜੈਂਸੀ ਛੱਤ ਸੇਵਾ · ਲੋਅਰ ਮੇਨਲੈਂਡ",
    title: "ਨੁਕਸਾਨ ਫੈਲਣ ਤੋਂ ਪਹਿਲਾਂ ਲੀਕ ਨੂੰ ਕਾਬੂ ਕਰੋ",
    body: "ਲੋਅਰ ਮੇਨਲੈਂਡ ਵਿੱਚ ਛੱਤ ਦੀ ਐਮਰਜੈਂਸੀ ਲੀਕ ਜਾਂਚ ਲਈ ਫ਼ੋਨ ਕਰੋ। ਜਵਾਬੀ ਸੇਵਾ ਟੀਮ ਦੀ ਉਪਲਬਧਤਾ, ਸੁਰੱਖਿਅਤ ਪਹੁੰਚ ਅਤੇ ਵਿਹਾਰਕ ਡਿਸਪੈਚ ਹਾਲਾਤਾਂ ਉੱਤੇ ਨਿਰਭਰ ਕਰਦੀ ਹੈ।",
    sectionEyebrow: "ਸਹੀ ਕਾਰਵਾਈ ਚੁਣੋ",
    sectionTitle: "ਛੱਤ ਦੇ ਉਸ ਹਿੱਸੇ ਤੋਂ ਸ਼ੁਰੂ ਕਰੋ ਜਿੱਥੇ ਅਸਲ ਨੁਕਸ ਪਿਆ ਹੈ",
    sectionBody:
      "ਲੀਕ ਦੀ ਜਾਂਚ, ਐਮਰਜੈਂਸੀ ਅਸਥਾਈ ਵਾਟਰਪ੍ਰੂਫਿੰਗ ਅਤੇ ਛੱਤ ਦੀ ਕਿਸਮ ਮੁਤਾਬਕ ਮੁਰੰਮਤ ਲਈ Revolin ਦੀਆਂ ਇਹ ਸਥਾਪਤ ਸੇਵਾਵਾਂ ਉਪਲਬਧ ਹਨ।",
    focus: [
      "ਇਮਾਰਤ ਵਿੱਚ ਦਾਖ਼ਲ ਹੋ ਰਿਹਾ ਪਾਣੀ",
      "ਤੂਫ਼ਾਨ ਅਤੇ ਹਵਾ ਦਾ ਨੁਕਸਾਨ",
      "ਫਲੈਸ਼ਿੰਗ, ਸਕਾਈਲਾਈਟ ਅਤੇ ਛੱਤ ਵਿਚਲੇ ਖੁੱਲ੍ਹੇ ਹਿੱਸੇ",
    ],
  },
  "commercial-roofing": {
    eyebrow: "ਵਪਾਰਕ ਛੱਤਾਂ · ਬ੍ਰਿਟਿਸ਼ ਕੋਲੰਬੀਆ",
    title: "ਇਮਾਰਤ ਦੀ ਲੰਬੀ ਮਿਆਦ ਦੀ ਕੀਮਤ ਨੂੰ ਧਿਆਨ ਵਿੱਚ ਰੱਖ ਕੇ ਫ਼ੈਸਲੇ",
    body: "ਚੱਲ ਰਹੀਆਂ ਵਪਾਰਕ ਇਮਾਰਤਾਂ ਲਈ ਜਾਂਚ-ਅਧਾਰਿਤ ਮੁਰੰਮਤ, ਬਦਲੀ ਅਤੇ ਰੱਖ-ਰਖਾਅ ਦੇ ਰਸਤੇ। ਲੋਅਰ ਮੇਨਲੈਂਡ ਤੋਂ ਬਾਹਰ ਤੈਅ ਕੀਤੇ ਕੰਮ ਦਾ ਫ਼ੈਸਲਾ ਕੰਮ ਦੇ ਦਾਇਰੇ, ਪਹੁੰਚ, ਸਾਈਟ ਤਿਆਰੀ ਅਤੇ ਸਮਾਂ-ਸਾਰਣੀ ਅਨੁਸਾਰ ਹੁੰਦਾ ਹੈ।",
    sectionEyebrow: "ਮੌਜੂਦਾ ਤਕਨੀਕੀ ਸੇਵਾਵਾਂ",
    sectionTitle: "ਵਪਾਰਕ ਲੋੜਾਂ ਲਈ ਜੁੜੀਆਂ ਹੋਈਆਂ ਭਰੋਸੇਯੋਗ ਸੇਵਾਵਾਂ",
    sectionBody:
      "ਵਪਾਰਕ ਪ੍ਰੋਜੈਕਟ Revolin ਦੀਆਂ ਮੌਜੂਦਾ ਘੱਟ ਢਲਾਣ, ਛੱਤ ਬਦਲੀ ਅਤੇ ਰੱਖ-ਰਖਾਅ ਸੇਵਾਵਾਂ ਨਾਲ ਜੋੜੇ ਜਾਂਦੇ ਹਨ, ਤਾਂ ਜੋ ਤਕਨੀਕੀ ਜਾਣਕਾਰੀ ਦੁਹਰਾਈ ਨਾ ਜਾਵੇ।",
    focus: [
      "ਡਰੇਨੇਜ ਅਤੇ ਮੈਂਬਰੇਨ ਦੀ ਹਾਲਤ",
      "ਚੱਲਦੀ ਇਮਾਰਤ ਵਿੱਚ ਕੰਮ ਦਾ ਤਾਲਮੇਲ",
      "ਉਮਰ ਅਤੇ ਰੱਖ-ਰਖਾਅ ਦੀ ਯੋਜਨਾ",
    ],
  },
  "strata-roofing": {
    eyebrow: "ਸਟ੍ਰਾਟਾ ਅਤੇ ਬਹੁ-ਪਰਿਵਾਰਕ ਇਮਾਰਤਾਂ",
    title: "ਕੌਂਸਲਾਂ ਅਤੇ ਪ੍ਰਾਪਰਟੀ ਮੈਨੇਜਰਾਂ ਲਈ ਵਧੇਰੇ ਸਪੱਸ਼ਟ ਰਾਹ",
    body: "ਸਟ੍ਰਾਟਾ ਅਤੇ ਬਹੁ-ਪਰਿਵਾਰਕ ਇਮਾਰਤਾਂ ਲਈ ਛੱਤ ਦੀ ਹਾਲਤ ਅਨੁਸਾਰ ਸਹਾਇਤਾ, ਜਿਸ ਵਿੱਚ ਵਸਨੀਕਾਂ ਦੀ ਪਹੁੰਚ, ਸਾਂਝੇ ਫ਼ੈਸਲੇ ਅਤੇ ਪੜਾਅਵਾਰ ਪ੍ਰੋਜੈਕਟ ਯੋਜਨਾ ਸ਼ਾਮਲ ਹੈ।",
    sectionEyebrow: "ਆਪਸ ਵਿੱਚ ਜੁੜੇ ਕੰਮ",
    sectionTitle: "ਇੱਕ ਇਮਾਰਤੀ ਰਣਨੀਤੀ, ਮਾਹਰ ਸੇਵਾਵਾਂ ਦੇ ਸਹਿਯੋਗ ਨਾਲ",
    sectionBody:
      "ਪ੍ਰੋਜੈਕਟ-ਵਿਸ਼ੇਸ਼ ਜਾਂਚ ਮੰਗਣ ਤੋਂ ਪਹਿਲਾਂ ਮੁਰੰਮਤ, ਰੱਖ-ਰਖਾਅ ਅਤੇ ਛੱਤ ਬਦਲੀ ਦੇ ਸੰਭਾਵੀ ਰਸਤਿਆਂ ਲਈ ਮੌਜੂਦਾ ਸੇਵਾ ਪੰਨੇ ਵੇਖੋ।",
    focus: ["ਕੌਂਸਲ ਲਈ ਸਪੱਸ਼ਟ ਨਤੀਜੇ", "ਵਸਨੀਕਾਂ ਅਤੇ ਪਹੁੰਚ ਦੀ ਯੋਜਨਾ", "ਪੂੰਜੀਗਤ ਪ੍ਰੋਜੈਕਟ ਦੇ ਪੜਾਅ"],
  },
  "residential-roofing": {
    eyebrow: "ਰਿਹਾਇਸ਼ੀ ਛੱਤਾਂ",
    title: "ਆਪਣੇ ਘਰ ਦਾ ਭਵਿੱਖ ਛੱਤ ਤੋਂ ਬਣਾਉਣਾ ਸ਼ੁਰੂ ਕਰੋ",
    body: "ਘਰ, ਮੌਜੂਦਾ ਛੱਤ ਦੀ ਬਣਤਰ ਅਤੇ ਬ੍ਰਿਟਿਸ਼ ਕੋਲੰਬੀਆ ਦੇ ਵੱਖ-ਵੱਖ ਮੌਸਮੀ ਹਾਲਾਤਾਂ ਮੁਤਾਬਕ ਮੁਰੰਮਤ, ਰੱਖ-ਰਖਾਅ ਅਤੇ ਉੱਚ-ਮਿਆਰੀ ਛੱਤ ਬਦਲੀ ਦੀ ਯੋਜਨਾ।",
    sectionEyebrow: "ਰਿਹਾਇਸ਼ੀ ਸੇਵਾਵਾਂ",
    sectionTitle: "ਪਹਿਲਾਂ ਅਸਲ ਲੋੜ ਚੁਣੋ, ਕੋਈ ਉਤਪਾਦ ਨਹੀਂ",
    sectionBody:
      "ਮੌਜੂਦਾ ਰਿਹਾਇਸ਼ੀ ਸੇਵਾਵਾਂ ਵੇਖੋ, ਫਿਰ ਇਹ ਪੱਕਾ ਕਰਨ ਲਈ ਜਾਂਚ ਮੰਗੋ ਕਿ ਦੇਖੀ ਗਈ ਹਾਲਤ ਲਈ ਕਿਹੜੀ ਛੱਤ ਪ੍ਰਣਾਲੀ ਅਤੇ ਕੰਮ ਦਾ ਦਾਇਰਾ ਢੁਕਵਾਂ ਹੈ।",
    focus: ["ਮੌਸਮ ਤੋਂ ਸੁਰੱਖਿਆ", "ਘਰ ਦੀ ਬਣਾਵਟ ਨਾਲ ਮੇਲ", "ਹਵਾਦਾਰੀ ਅਤੇ ਪਾਣੀ ਦਾ ਪ੍ਰਬੰਧ"],
  },
  "roof-systems": {
    eyebrow: "ਛੱਤ ਪ੍ਰਣਾਲੀਆਂ",
    title: "ਸਹੀ ਛੱਤ ਇੱਕ ਪੂਰੀ, ਤਾਲਮੇਲ ਵਾਲੀ ਪ੍ਰਣਾਲੀ ਹੁੰਦੀ ਹੈ",
    body: "ਸ਼ਿੰਗਲ, ਸੀਡਰ, ਧਾਤ ਅਤੇ ਘੱਟ ਢਲਾਣ ਵਾਲੀਆਂ ਛੱਤਾਂ ਦੀਆਂ ਮੌਜੂਦਾ ਸੇਵਾਵਾਂ ਦੀ ਤੁਲਨਾ ਕਰੋ। ਚੋਣ ਛੱਤ ਦੀ ਬਣਤਰ, ਹੇਠਲੀ ਸਤਹ, ਡਰੇਨੇਜ, ਮੌਸਮੀ ਅਸਰ ਅਤੇ ਡਿਜ਼ਾਈਨ ਦੇ ਉਦੇਸ਼ ਉੱਤੇ ਨਿਰਭਰ ਕਰਦੀ ਹੈ।",
    sectionEyebrow: "ਮੁੱਖ ਪ੍ਰਣਾਲੀ ਪੰਨੇ",
    sectionTitle: "ਚਾਰ ਵੱਖਰੀਆਂ ਪ੍ਰਣਾਲੀਆਂ - ਹਰ ਇਮਾਰਤ ਲਈ ਇੱਕੋ ਜਵਾਬ ਨਹੀਂ",
    sectionBody:
      "ਹਰ ਪੰਨਾ ਦੱਸਦਾ ਹੈ ਕਿ ਉਹ ਪ੍ਰਣਾਲੀ ਕਿੱਥੇ ਢੁਕਵੀਂ ਹੈ ਅਤੇ ਮੁਰੰਮਤ ਜਾਂ ਬਦਲੀ ਦੀ ਸਿਫ਼ਾਰਸ਼ ਤੋਂ ਪਹਿਲਾਂ ਕੀ ਜਾਂਚਿਆ ਜਾਣਾ ਚਾਹੀਦਾ ਹੈ।",
    focus: [
      "ਢਲਾਣ ਵਾਲੀਆਂ ਛੱਤਾਂ ਦੀ ਪਰਤ",
      "ਘੱਟ ਢਲਾਣ ਵਾਲੇ ਮੈਂਬਰੇਨ",
      "ਕਿਨਾਰੇ, ਡਰੇਨੇਜ ਅਤੇ ਛੱਤ ਵਿਚਲੇ ਖੁੱਲ੍ਹੇ ਹਿੱਸੇ",
    ],
  },
  "roof-estimate": {
    eyebrow: "ਛੱਤ ਪ੍ਰੋਜੈਕਟ ਦੀ ਜਾਣਕਾਰੀ",
    title: "ਲਾਭਦਾਇਕ ਅੰਦਾਜ਼ਾ ਸਹੀ ਜਾਣਕਾਰੀ ਨਾਲ ਸ਼ੁਰੂ ਹੁੰਦਾ ਹੈ",
    body: "ਸਾਨੂੰ ਪ੍ਰਾਪਰਟੀ, ਛੱਤ ਅਤੇ ਲੋੜੀਂਦੇ ਸਮੇਂ ਬਾਰੇ ਦੱਸੋ। Revolin ਜਾਣਕਾਰੀ ਦੀ ਸਮੀਖਿਆ ਕਰਕੇ ਅਗਲਾ ਕਦਮ ਦੱਸੇਗਾ; ਇਹ ਪੰਨਾ ਬਿਨਾਂ ਪੁਸ਼ਟੀ ਤੁਰੰਤ ਕੀਮਤ ਨਹੀਂ ਬਣਾਉਂਦਾ।",
    sectionEyebrow: "ਜਮ੍ਹਾਂ ਕਰਨ ਤੋਂ ਪਹਿਲਾਂ",
    sectionTitle: "ਪ੍ਰੋਜੈਕਟ ਨੂੰ ਸਮਝਣ ਵਿੱਚ ਕਿਹੜੀ ਜਾਣਕਾਰੀ ਮਦਦ ਕਰਦੀ ਹੈ",
    sectionBody:
      "ਜੋ ਜਾਣਕਾਰੀ ਤੁਹਾਡੇ ਕੋਲ ਹੈ, ਉਹ ਸਾਂਝੀ ਕਰੋ। ਸ਼ੁਰੂ ਕਰਨ ਲਈ ਸਹੀ ਮਾਪ ਜਾਂ ਪਹਿਲਾਂ ਚੁਣੀ ਸਮੱਗਰੀ ਲਾਜ਼ਮੀ ਨਹੀਂ, ਅਤੇ ਅਧੂਰੀ ਜਾਣਕਾਰੀ ਤੋਂ ਕੋਈ ਬਣਾਵਟੀ ਕੀਮਤ-ਹੱਦ ਨਹੀਂ ਦਿੱਤੀ ਜਾਂਦੀ।",
    focus: [
      "ਪ੍ਰਾਪਰਟੀ ਦੀ ਥਾਂ ਅਤੇ ਕਿਸਮ",
      "ਮੌਜੂਦਾ ਛੱਤ ਅਤੇ ਜਾਣੀਆਂ ਸਮੱਸਿਆਵਾਂ",
      "ਸਮਾਂ, ਪਹੁੰਚ ਅਤੇ ਮਦਦਗਾਰ ਤਸਵੀਰਾਂ",
    ],
  },
  "british-columbia": {
    eyebrow: "ਤੈਅ ਛੱਤ ਪ੍ਰੋਜੈਕਟ · ਬ੍ਰਿਟਿਸ਼ ਕੋਲੰਬੀਆ",
    title: "ਬੀ.ਸੀ. ਦੇ ਹਾਲਾਤਾਂ ਲਈ ਯੋਜਨਾਬੱਧ ਛੱਤ ਪ੍ਰੋਜੈਕਟ",
    body: "ਐਮਰਜੈਂਸੀ ਜਵਾਬ ਲੋਅਰ ਮੇਨਲੈਂਡ ਉੱਤੇ ਕੇਂਦਰਿਤ ਹੈ। ਬ੍ਰਿਟਿਸ਼ ਕੋਲੰਬੀਆ ਭਰ ਦੇ ਤੈਅ ਰਿਹਾਇਸ਼ੀ, ਵਪਾਰਕ, ਸਟ੍ਰਾਟਾ ਅਤੇ ਵਿਸ਼ੇਸ਼ ਪ੍ਰੋਜੈਕਟਾਂ ਬਾਰੇ ਵੀ ਵਿਚਾਰ ਕੀਤਾ ਜਾ ਸਕਦਾ ਹੈ।",
    sectionEyebrow: "ਸੇਵਾ ਕਵਰੇਜ ਦਾ ਢੰਗ",
    sectionTitle: "ਸਥਾਨਕ ਐਮਰਜੈਂਸੀ ਜਵਾਬ, ਸੂਬੇ ਭਰ ਦੇ ਪ੍ਰੋਜੈਕਟਾਂ ਦੀ ਯੋਜਨਾ",
    sectionBody:
      "ਲੋਅਰ ਮੇਨਲੈਂਡ ਤੋਂ ਬਾਹਰ ਕੰਮ ਦੀ ਉਪਲਬਧਤਾ ਪ੍ਰੋਜੈਕਟ ਦੇ ਦਾਇਰੇ, ਪਹੁੰਚ, ਸਾਈਟ ਤਿਆਰੀ, ਆਵਾਜਾਈ ਅਤੇ ਸਮਾਂ-ਸਾਰਣੀ ਉੱਤੇ ਨਿਰਭਰ ਕਰਦੀ ਹੈ। ਕਿਸੇ ਖੇਤਰ ਦਾ ਨਾਮ ਸੇਵਾ ਦੀ ਸੰਭਾਵਨਾ ਦੱਸਦਾ ਹੈ, ਸਥਾਨਕ ਦਫ਼ਤਰ ਨਹੀਂ।",
    focus: [
      "ਲੋਅਰ ਮੇਨਲੈਂਡ ਵਿੱਚ ਐਮਰਜੈਂਸੀ ਜਵਾਬ",
      "ਸੂਬੇ ਭਰ ਵਿੱਚ ਤੈਅ ਕੰਮ",
      "ਕੰਮ ਦੇ ਦਾਇਰੇ ਅਨੁਸਾਰ ਸਾਈਟ ਤਿਆਰੀ ਦੀ ਸਮੀਖਿਆ",
    ],
  },
};

const koreanCopy: Record<ExpansionPageKind, PageCopy> = {
  "emergency-roofing": {
    eyebrow: "긴급 지붕 서비스 · 로어 메인랜드",
    title: "피해가 번지기 전에 누수부터 막으세요",
    body: "로어 메인랜드 전역에서 긴급 지붕 누수 점검을 요청할 수 있습니다. 실제 출동 여부는 작업팀 일정, 안전한 접근 가능 여부, 현장 출동 여건에 따라 달라집니다.",
    sectionEyebrow: "상황에 맞는 대응 선택",
    sectionTitle: "문제가 발생한 지붕 부위부터 확인합니다",
    sectionBody:
      "누수 원인 조사, 긴급 임시 방수, 지붕 시스템별 보수는 Revolin이 현재 제공하는 해당 전문 서비스로 연결됩니다.",
    focus: ["현재 진행 중인 빗물 유입", "폭풍 및 강풍 피해", "후레싱·천창·지붕 관통부"],
  },
  "commercial-roofing": {
    eyebrow: "상업용 지붕 · 브리티시컬럼비아",
    title: "건물의 자산 가치를 중심에 둔 지붕 의사결정",
    body: "운영 중인 상업용 건물을 위한 점검 기반 보수·교체·유지관리 경로입니다. 로어 메인랜드 외 지역의 예약 공사는 범위, 접근성, 현장 준비, 이동 및 일정 조건에 따라 검토합니다.",
    sectionEyebrow: "현재 제공 중인 전문 서비스",
    sectionTitle: "상업용 수요에 맞춰 검증된 서비스를 연결합니다",
    sectionBody:
      "상업용 프로젝트는 기술 설명을 중복하지 않고 Revolin의 기존 저경사 지붕, 교체 및 유지관리 서비스로 연결됩니다.",
    focus: ["배수와 방수막 상태", "건물 운영 중 현장 조율", "수명주기와 유지관리 계획"],
  },
  "strata-roofing": {
    eyebrow: "스트라타 및 다세대 건물",
    title: "위원회와 관리자를 위한 더 명확한 지붕 관리 경로",
    body: "입주민 출입, 공동 의사결정, 단계별 공사 계획을 고려하여 스트라타와 다세대 건물의 실제 지붕 상태에 맞춘 지원을 제공합니다.",
    sectionEyebrow: "서로 연결된 공사 범위",
    sectionTitle: "하나의 건물 전략을 전문 서비스로 뒷받침합니다",
    sectionBody:
      "프로젝트별 현장 점검을 요청하기 전에 기존 서비스 페이지에서 가능한 보수·유지관리·교체 경로를 확인해 보세요.",
    focus: ["위원회 검토에 적합한 조사 결과", "입주민 안내와 출입 계획", "자본 공사의 단계별 계획"],
  },
  "residential-roofing": {
    eyebrow: "주택 지붕",
    title: "지붕에서부터 집의 미래를 설계하세요",
    body: "주택의 구조와 기존 지붕 구성, 브리티시컬럼비아 지역별 노출 조건에 맞춰 보수·유지관리·프리미엄 교체를 계획합니다.",
    sectionEyebrow: "주택 지붕 서비스",
    sectionTitle: "제품을 먼저 정하지 말고 필요한 작업부터 확인하세요",
    sectionBody:
      "현재 제공 중인 주택 서비스를 살펴본 뒤 현장 점검을 요청하여 실제 상태에 맞는 시스템과 공사 범위를 확인하세요.",
    focus: ["기후로부터의 보호", "건축 디자인과의 조화", "환기와 빗물 관리"],
  },
  "roof-systems": {
    eyebrow: "지붕 시스템",
    title: "좋은 지붕은 완성도 높은 하나의 시스템입니다",
    body: "아스팔트 슁글, 시더, 금속, 저경사 지붕의 기존 서비스 경로를 비교해 보세요. 시스템은 지붕 형태, 바탕면, 배수, 노출 조건, 디자인 의도에 따라 선택합니다.",
    sectionEyebrow: "공식 시스템 안내",
    sectionTitle: "서로 다른 네 가지 시스템, 모든 건물에 맞는 하나의 답은 없습니다",
    sectionBody:
      "각 페이지에서 해당 시스템이 적합한 조건과 보수 또는 교체를 제안하기 전에 확인해야 할 항목을 설명합니다.",
    focus: ["경사 지붕 마감재", "저경사 지붕 방수막", "가장자리·배수·관통부 디테일"],
  },
  "roof-estimate": {
    eyebrow: "지붕 프로젝트 접수",
    title: "유용한 견적은 정확한 프로젝트 정보에서 시작됩니다",
    body: "건물과 지붕 상태, 희망 일정을 알려 주세요. Revolin이 내용을 검토해 다음 단계를 안내하며, 이 페이지는 확인되지 않은 즉석 가격을 생성하지 않습니다.",
    sectionEyebrow: "제출 전 확인",
    sectionTitle: "프로젝트 파악에 도움이 되는 정보",
    sectionBody:
      "현재 알고 있는 내용만 보내셔도 됩니다. 정확한 치수나 자재 선택이 없어도 상담을 시작할 수 있으며, 불완전한 정보로 임의의 가격 범위를 제시하지 않습니다.",
    focus: ["건물 위치와 유형", "현재 지붕과 확인된 문제", "희망 일정·현장 접근·참고 사진"],
  },
  "british-columbia": {
    eyebrow: "예약 지붕 공사 · 브리티시컬럼비아",
    title: "BC 환경에 맞춰 계획하는 지붕 프로젝트",
    body: "긴급 출동은 로어 메인랜드를 중심으로 운영합니다. 브리티시컬럼비아 전역의 예약형 주택·상업용·스트라타·전문 프로젝트도 검토할 수 있습니다.",
    sectionEyebrow: "서비스 지역 운영 방식",
    sectionTitle: "긴급 대응은 지역 중심으로, 프로젝트 계획은 BC 전역으로",
    sectionBody:
      "로어 메인랜드 외 지역의 공사 가능 여부는 범위, 접근성, 현장 준비, 물류와 일정에 따라 달라집니다. 지역명은 검토 가능한 서비스 범위를 뜻하며 현지 지점이 있다는 의미는 아닙니다.",
    focus: [
      "로어 메인랜드 긴급 대응",
      "BC 전역의 예약 공사",
      "공사 범위에 따른 이동·현장 준비 검토",
    ],
  },
};

function copyFor(kind: ExpansionPageKind, locale: LocaleCode): PageCopy {
  if (locale === "zh-cn") return simplifiedChineseCopy[kind];
  if (locale === "zh-tw") return traditionalChineseCopy[kind];
  if (locale === "pa-in") return punjabiCopy[kind];
  if (locale === "ko-kr") return koreanCopy[kind];
  if (locale === "fa-ir") return persianCopy[kind];
  if (locale === "ar-sa") return arabicCopy[kind];
  return englishCopy[kind];
}

const sharedCopy = {
  "en-ca": {
    home: "Home",
    services: "Roofing services",
    estimate: "Request an assessment",
    urgent: "Call for urgent help",
    explore: "View all services",
    availability: "Service availability",
    emergency:
      "Emergency and urgent response is available throughout the Lower Mainland, subject to crew availability and practical dispatch capability.",
    planned:
      "Scheduled roofing projects are considered throughout British Columbia, subject to scope, access, mobilization, logistics and scheduling.",
    mobilization:
      "Mobilization and minimum project requirements may apply outside the Lower Mainland.",
    focus: "What we consider",
    related: "Established services for this pathway",
    relatedBody: "Open a service to review its scope, assessment criteria and next steps.",
    learn: "Explore service",
    formTitle: "Tell us about the roof",
    formBody:
      "Use the existing secure project form. For active water entry, call first so the team can confirm practical availability.",
    regions: "Regions considered for scheduled projects",
    regionNote:
      "Region names describe potential service coverage only; they do not represent branch offices.",
    citiesTitle: "Confirmed Lower Mainland city pages",
    citiesBody:
      "14 confirmed Lower Mainland cities already have dedicated service-area pages. Open the index or start with a nearby city.",
    citiesIndex: "View all service areas",
  },
  "zh-cn": {
    home: "首页",
    services: "屋顶服务",
    estimate: "申请现场评估",
    urgent: "致电申请紧急协助",
    explore: "查看全部服务",
    availability: "服务覆盖说明",
    emergency:
      "低陆平原全境可提供紧急响应，但实际安排取决于施工团队档期及现场能否安全、合理地调度。",
    planned:
      "不列颠哥伦比亚省各地的预约屋顶项目可提出咨询，是否承接取决于工程范围、现场通行、进场组织、物流和档期。",
    mobilization: "低陆平原以外的项目可能设有最低工程规模和进场要求。",
    focus: "我们会考虑",
    related: "此路径下已正式提供的服务",
    relatedBody: "打开服务页面，了解工程范围、评估标准和下一步安排。",
    learn: "了解服务",
    formTitle: "告诉我们屋顶的情况",
    formBody: "请使用现有的安全项目表格。如有正在发生的进水，请先致电，以便团队确认实际响应条件。",
    regions: "可评估预约项目的地区",
    regionNote: "地区名称仅表示潜在服务范围，并不代表当地设有分公司。",
    citiesTitle: "已上线的低陆平原城市页面",
    citiesBody: "低陆平原已有 14 个确认城市设有独立服务区域页面。可先查看总览，或从附近城市开始。",
    citiesIndex: "查看全部服务区域",
  },
  "zh-tw": {
    home: "首頁",
    services: "屋頂服務",
    estimate: "申請現場評估",
    urgent: "致電申請緊急協助",
    explore: "查看全部服務",
    availability: "服務覆蓋說明",
    emergency:
      "低陸平原全境可提供緊急回應，但實際安排取決於施工團隊檔期及現場能否安全、合理地調度。",
    planned:
      "卑詩省各地的預約屋頂項目均可提出查詢，能否承接取決於工程範圍、現場通行、進場安排、物流及檔期。",
    mobilization: "低陸平原以外的項目可能設有最低工程規模及進場要求。",
    focus: "我們會考慮",
    related: "此路徑下已正式提供的服務",
    relatedBody: "開啟服務頁面，了解工程範圍、評估準則及下一步安排。",
    learn: "了解服務",
    formTitle: "告訴我們屋頂的情況",
    formBody: "請使用現有的安全項目表格。如有正在發生的進水，請先致電，以便團隊確認實際回應條件。",
    regions: "可評估預約項目的地區",
    regionNote: "地區名稱只表示潛在服務範圍，並不代表當地設有分公司。",
    citiesTitle: "已上線的低陸平原城市頁面",
    citiesBody: "低陸平原已有 14 個確認城市設有獨立服務區域頁面。可先查看總覽，或從附近城市開始。",
    citiesIndex: "查看全部服務區域",
  },
  "pa-in": {
    home: "ਮੁੱਖ ਪੰਨਾ",
    services: "ਛੱਤ ਸੇਵਾਵਾਂ",
    estimate: "ਜਾਂਚ ਲਈ ਬੇਨਤੀ ਕਰੋ",
    urgent: "ਤੁਰੰਤ ਮਦਦ ਲਈ ਫ਼ੋਨ ਕਰੋ",
    explore: "ਸਾਰੀਆਂ ਸੇਵਾਵਾਂ ਵੇਖੋ",
    availability: "ਸੇਵਾ ਦੀ ਉਪਲਬਧਤਾ",
    emergency:
      "ਲੋਅਰ ਮੇਨਲੈਂਡ ਭਰ ਵਿੱਚ ਐਮਰਜੈਂਸੀ ਅਤੇ ਤੁਰੰਤ ਜਵਾਬੀ ਸੇਵਾ ਟੀਮ ਦੀ ਉਪਲਬਧਤਾ ਅਤੇ ਵਿਹਾਰਕ ਡਿਸਪੈਚ ਸਮਰੱਥਾ ਅਨੁਸਾਰ ਦਿੱਤੀ ਜਾਂਦੀ ਹੈ।",
    planned:
      "ਬ੍ਰਿਟਿਸ਼ ਕੋਲੰਬੀਆ ਭਰ ਵਿੱਚ ਤੈਅ ਛੱਤ ਪ੍ਰੋਜੈਕਟਾਂ ਬਾਰੇ ਕੰਮ ਦੇ ਦਾਇਰੇ, ਪਹੁੰਚ, ਸਾਈਟ ਤਿਆਰੀ, ਆਵਾਜਾਈ ਅਤੇ ਸਮਾਂ-ਸਾਰਣੀ ਮੁਤਾਬਕ ਵਿਚਾਰ ਕੀਤਾ ਜਾਂਦਾ ਹੈ।",
    mobilization:
      "ਲੋਅਰ ਮੇਨਲੈਂਡ ਤੋਂ ਬਾਹਰ ਸਾਈਟ ਤਿਆਰੀ ਅਤੇ ਘੱਟੋ-ਘੱਟ ਪ੍ਰੋਜੈਕਟ ਆਕਾਰ ਦੀਆਂ ਸ਼ਰਤਾਂ ਲਾਗੂ ਹੋ ਸਕਦੀਆਂ ਹਨ।",
    focus: "ਅਸੀਂ ਕੀ ਵਿਚਾਰਦੇ ਹਾਂ",
    related: "ਇਸ ਰਸਤੇ ਲਈ ਮੌਜੂਦਾ ਸੇਵਾਵਾਂ",
    relatedBody: "ਕੰਮ ਦਾ ਦਾਇਰਾ, ਜਾਂਚ ਦੇ ਮਾਪਦੰਡ ਅਤੇ ਅਗਲੇ ਕਦਮ ਜਾਣਨ ਲਈ ਸੇਵਾ ਪੰਨਾ ਖੋਲ੍ਹੋ।",
    learn: "ਸੇਵਾ ਵੇਖੋ",
    formTitle: "ਸਾਨੂੰ ਛੱਤ ਬਾਰੇ ਦੱਸੋ",
    formBody:
      "ਮੌਜੂਦਾ ਸੁਰੱਖਿਅਤ ਪ੍ਰੋਜੈਕਟ ਫਾਰਮ ਵਰਤੋ। ਜੇ ਪਾਣੀ ਅੰਦਰ ਆ ਰਿਹਾ ਹੈ ਤਾਂ ਵਿਹਾਰਕ ਉਪਲਬਧਤਾ ਦੀ ਪੁਸ਼ਟੀ ਲਈ ਪਹਿਲਾਂ ਫ਼ੋਨ ਕਰੋ।",
    regions: "ਤੈਅ ਪ੍ਰੋਜੈਕਟਾਂ ਲਈ ਵਿਚਾਰੇ ਜਾਂਦੇ ਖੇਤਰ",
    regionNote: "ਖੇਤਰਾਂ ਦੇ ਨਾਮ ਸਿਰਫ਼ ਸੰਭਾਵੀ ਸੇਵਾ ਕਵਰੇਜ ਦੱਸਦੇ ਹਨ; ਇਹ ਸ਼ਾਖਾ ਦਫ਼ਤਰ ਨਹੀਂ ਹਨ।",
    citiesTitle: "ਲੋਅਰ ਮੇਨਲੈਂਡ ਦੇ ਪੁਸ਼ਟੀਸ਼ੁਦਾ ਸ਼ਹਿਰੀ ਪੰਨੇ",
    citiesBody:
      "ਲੋਅਰ ਮੇਨਲੈਂਡ ਦੇ 14 ਪੁਸ਼ਟੀਸ਼ੁਦਾ ਸ਼ਹਿਰਾਂ ਦੇ ਵੱਖਰੇ ਸੇਵਾ-ਖੇਤਰ ਪੰਨੇ ਪਹਿਲਾਂ ਹੀ ਮੌਜੂਦ ਹਨ। ਸੂਚੀ ਖੋਲ੍ਹੋ ਜਾਂ ਨੇੜਲੇ ਸ਼ਹਿਰ ਤੋਂ ਸ਼ੁਰੂ ਕਰੋ।",
    citiesIndex: "ਸਾਰੇ ਸੇਵਾ ਖੇਤਰ ਵੇਖੋ",
  },
  "ko-kr": {
    home: "홈",
    services: "지붕 서비스",
    estimate: "현장 점검 요청",
    urgent: "긴급 지원 전화",
    explore: "전체 서비스 보기",
    availability: "서비스 가능 지역",
    emergency:
      "로어 메인랜드 전역에서 긴급 대응을 제공하며, 실제 출동은 작업팀 일정과 현장 여건에 따라 달라집니다.",
    planned:
      "브리티시컬럼비아 전역의 예약 지붕 프로젝트는 공사 범위, 접근성, 현장 준비, 물류 및 일정에 따라 검토합니다.",
    mobilization:
      "로어 메인랜드 외 지역에는 이동·현장 준비 및 최소 프로젝트 규모 조건이 적용될 수 있습니다.",
    focus: "검토 사항",
    related: "이 경로에서 현재 제공하는 서비스",
    relatedBody: "서비스 페이지에서 공사 범위, 점검 기준과 다음 단계를 확인하세요.",
    learn: "서비스 보기",
    formTitle: "지붕 상태를 알려 주세요",
    formBody:
      "현재의 보안 프로젝트 양식을 이용해 주세요. 빗물이 유입 중이라면 먼저 전화하여 실제 출동 가능 여부를 확인하세요.",
    regions: "예약 프로젝트 검토 가능 지역",
    regionNote: "지역명은 잠재적인 서비스 범위만을 뜻하며, 현지 지점이 있다는 의미는 아닙니다.",
    citiesTitle: "확인된 로어 메인랜드 도시 페이지",
    citiesBody:
      "로어 메인랜드의 확인된 14개 도시는 이미 전용 서비스 지역 페이지가 있습니다. 목록을 열거나 가까운 도시부터 확인하세요.",
    citiesIndex: "전체 서비스 지역 보기",
  },
  "fa-ir": {
    home: "خانه",
    services: "خدمات سقف",
    estimate: "درخواست ارزیابی",
    urgent: "تماس برای کمک فوری",
    explore: "مشاهده همه خدمات",
    availability: "محدوده ارائه خدمات",
    emergency:
      "پاسخ‌گویی به درخواست‌های فوری و اضطراری در سراسر لوئر مین‌لند، با توجه به ظرفیت تیم و امکان عملی اعزام، انجام می‌شود.",
    planned:
      "پروژه‌های برنامه‌ریزی‌شده در سراسر بریتیش کلمبیا، با توجه به دامنه کار، دسترسی، تجهیز، تدارکات و زمان‌بندی بررسی می‌شوند.",
    mobilization:
      "برای پروژه‌های خارج از لوئر مین‌لند ممکن است حداقل اندازه پروژه و هزینه تجهیز و اعزام در نظر گرفته شود.",
    focus: "مواردی که بررسی می‌کنیم",
    related: "خدمات اصلی مرتبط با این مسیر",
    relatedBody:
      "برای آشنایی با دامنه کار، معیارهای ارزیابی و مرحله بعد، صفحه هر خدمت را باز کنید.",
    learn: "مشاهده خدمت",
    formTitle: "درباره سقف برای ما بنویسید",
    formBody:
      "از فرم امن فعلی پروژه استفاده کنید. اگر آب وارد ساختمان می‌شود، ابتدا تماس بگیرید تا امکان اعزام بررسی شود.",
    regions: "مناطق قابل بررسی برای پروژه‌های برنامه‌ریزی‌شده",
    regionNote:
      "نام مناطق فقط محدوده احتمالی خدمات را نشان می‌دهد و به‌معنای وجود شعبه رولین نیست.",
    citiesTitle: "صفحات تأییدشدهٔ شهرهای لوئر مین‌لند",
    citiesBody:
      "۱۴ شهر تأییدشده در لوئر مین‌لند همین حالا صفحهٔ اختصاصی محدوده خدمات دارند. فهرست را باز کنید یا از یک شهر نزدیک شروع کنید.",
    citiesIndex: "مشاهده همه مناطق خدماتی",
  },
  "ar-sa": {
    home: "الرئيسية",
    services: "خدمات الأسقف",
    estimate: "طلب تقييم",
    urgent: "اتصل للمساعدة العاجلة",
    explore: "عرض جميع الخدمات",
    availability: "نطاق توفر الخدمة",
    emergency:
      "تتوفر الاستجابة العاجلة والطارئة في أنحاء لوَر مينلاند، وفق توافر الطاقم وإمكانية الإرسال العملية.",
    planned:
      "تُبحث مشاريع الأسقف المجدولة في أنحاء كولومبيا البريطانية وفق النطاق والوصول والتجهيز والخدمات اللوجستية والجدول الزمني.",
    mobilization: "قد تسري متطلبات حد أدنى للمشروع وتكاليف تجهيز خارج لوَر مينلاند.",
    focus: "ما الذي نراجعه",
    related: "الخدمات الأساسية لهذا المسار",
    relatedBody: "افتح صفحة الخدمة لمراجعة نطاقها ومعايير التقييم والخطوة التالية.",
    learn: "استكشف الخدمة",
    formTitle: "أخبرنا عن السقف",
    formBody:
      "استخدم نموذج المشروع الآمن الحالي. عند دخول المياه إلى المبنى، اتصل أولاً لتأكيد إمكانية الاستجابة العملية.",
    regions: "مناطق يمكن بحث المشاريع المجدولة فيها",
    regionNote: "تشير أسماء المناطق إلى نطاق خدمة محتمل فقط، ولا تعني وجود فروع محلية.",
    citiesTitle: "صفحات المدن المؤكدة في لوَر مينلاند",
    citiesBody:
      "توجد حالياً صفحات مخصصة لمناطق الخدمة في 14 مدينة مؤكدة في لوَر مينلاند. افتح الفهرس أو ابدأ بمدينة قريبة.",
    citiesIndex: "عرض جميع مناطق الخدمة",
  },
} as const;

function commonFor(locale: LocaleCode) {
  return sharedCopy[locale];
}

const localizedRegionNames: Partial<
  Record<LocaleCode, Partial<Record<(typeof bcCoverageRegions)[number]["id"], string>>>
> = {
  "zh-cn": {
    "lower-mainland-fraser-valley": "低陆平原与菲沙河谷",
    "sea-to-sky": "海天走廊",
    "sunshine-coast": "阳光海岸",
    "greater-victoria": "大维多利亚地区",
    "vancouver-island": "温哥华岛",
    okanagan: "奥肯那根",
    "thompson-shuswap": "汤普森 - 舒斯瓦普",
    kootenays: "库特尼地区",
    "central-bc": "不列颠哥伦比亚省中部",
    "northwest-bc": "不列颠哥伦比亚省西北部",
    "northeast-bc": "不列颠哥伦比亚省东北部",
  },
  "zh-tw": {
    "lower-mainland-fraser-valley": "低陸平原與菲沙河谷",
    "sea-to-sky": "海天走廊",
    "sunshine-coast": "陽光海岸",
    "greater-victoria": "大維多利亞地區",
    "vancouver-island": "溫哥華島",
    okanagan: "奧肯那根",
    "thompson-shuswap": "湯普森 - 舒斯瓦普",
    kootenays: "庫特尼地區",
    "central-bc": "卑詩省中部",
    "northwest-bc": "卑詩省西北部",
    "northeast-bc": "卑詩省東北部",
  },
  "pa-in": {
    "lower-mainland-fraser-valley": "ਲੋਅਰ ਮੇਨਲੈਂਡ ਅਤੇ ਫ੍ਰੇਜ਼ਰ ਵੈਲੀ",
    "sea-to-sky": "ਸੀ ਟੂ ਸਕਾਈ",
    "sunshine-coast": "ਸਨਸ਼ਾਈਨ ਕੋਸਟ",
    "greater-victoria": "ਗ੍ਰੇਟਰ ਵਿਕਟੋਰੀਆ",
    "vancouver-island": "ਵੈਨਕੂਵਰ ਆਇਲੈਂਡ",
    okanagan: "ਓਕਾਨਾਗਨ",
    "thompson-shuswap": "ਥਾਮਸਨ–ਸ਼ੂਸਵਾਪ",
    kootenays: "ਕੂਟਨੇਜ਼",
    "central-bc": "ਕੇਂਦਰੀ ਬ੍ਰਿਟਿਸ਼ ਕੋਲੰਬੀਆ",
    "northwest-bc": "ਉੱਤਰ-ਪੱਛਮੀ ਬ੍ਰਿਟਿਸ਼ ਕੋਲੰਬੀਆ",
    "northeast-bc": "ਉੱਤਰ-ਪੂਰਬੀ ਬ੍ਰਿਟਿਸ਼ ਕੋਲੰਬੀਆ",
  },
  "ko-kr": {
    "lower-mainland-fraser-valley": "로어 메인랜드 및 프레이저 밸리",
    "sea-to-sky": "시 투 스카이",
    "sunshine-coast": "선샤인 코스트",
    "greater-victoria": "그레이터 빅토리아",
    "vancouver-island": "밴쿠버 아일랜드",
    okanagan: "오카나간",
    "thompson-shuswap": "톰슨–슈스왑",
    kootenays: "쿠트니",
    "central-bc": "브리티시컬럼비아 중부",
    "northwest-bc": "브리티시컬럼비아 북서부",
    "northeast-bc": "브리티시컬럼비아 북동부",
  },
  "fa-ir": {
    "lower-mainland-fraser-valley": "لوئر مین‌لند و دره فریزر",
    "sea-to-sky": "سی تو اسکای",
    "sunshine-coast": "سان‌شاین کوست",
    "greater-victoria": "ویکتوریای بزرگ",
    "vancouver-island": "جزیره ونکوور",
    okanagan: "اوکاناگان",
    "thompson-shuswap": "تامپسون و شوسواپ",
    kootenays: "کوتنیز",
    "central-bc": "مرکز بریتیش کلمبیا",
    "northwest-bc": "شمال‌غرب بریتیش کلمبیا",
    "northeast-bc": "شمال‌شرق بریتیش کلمبیا",
  },
  "ar-sa": {
    "lower-mainland-fraser-valley": "لوَر مينلاند ووادي فريزر",
    "sea-to-sky": "سي تو سكاي",
    "sunshine-coast": "ساحل صن شاين",
    "greater-victoria": "فيكتوريا الكبرى",
    "vancouver-island": "جزيرة فانكوفر",
    okanagan: "أوكاناغان",
    "thompson-shuswap": "طومسون وشوسواب",
    kootenays: "كوتينايز",
    "central-bc": "وسط كولومبيا البريطانية",
    "northwest-bc": "شمال غرب كولومبيا البريطانية",
    "northeast-bc": "شمال شرق كولومبيا البريطانية",
  },
};

export function ExpansionPage({ kind, path, locale, ui }: ExpansionPageProps) {
  const base = basePages[kind];
  const copy = copyFor(kind, locale);
  const common = commonFor(locale);
  const Icon = base.icon;
  const services = base.serviceSlugs
    .map((slug) => serviceBySlug[slug])
    .filter((service): service is NonNullable<typeof service> => Boolean(service))
    .map((service) => localizeServicePage(service, locale));
  const isEmergency = kind === "emergency-roofing";
  const isBcHub = kind === "british-columbia";

  return (
    <>
      <section className="relative isolate min-h-[42rem] overflow-hidden bg-slate-deep pt-28 text-primary-foreground md:pt-36">
        <img
          src={base.image}
          alt={base.imageAlt}
          width={1536}
          height={1024}
          fetchPriority="high"
          className="absolute inset-0 -z-20 size-full object-cover"
        />
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(100deg,oklch(0.13_0.045_255/0.98)_0%,oklch(0.14_0.05_255/0.86)_54%,oklch(0.14_0.05_255/0.34)_100%)]" />
        <div className="container-page flex min-h-[34rem] flex-col justify-between py-10 md:py-16">
          <nav aria-label="Breadcrumb" className="text-xs text-primary-foreground/60">
            <a href={path("/")} className="underline-offset-4 hover:underline">
              {ui?.common.home ?? common.home}
            </a>
            <span aria-hidden="true" className="mx-2">
              /
            </span>
            <span aria-current="page">{copy.eyebrow}</span>
          </nav>

          <div className="max-w-4xl py-14 md:py-20">
            <div className="flex items-center gap-3">
              <span className="grid size-10 place-items-center rounded-full border border-gold/50 bg-slate-deep/55 backdrop-blur-sm">
                <Icon className="size-5 text-gold" aria-hidden="true" />
              </span>
              <p className="eyebrow text-gold">{copy.eyebrow}</p>
            </div>
            <h1 className="display-1 mt-6 max-w-3xl text-balance">{copy.title}</h1>
            <p className="mt-6 max-w-2xl text-base leading-8 text-primary-foreground/78 md:text-lg">
              {copy.body}
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              {isEmergency ? (
                <Button variant="accent" size="xl" asChild>
                  <a
                    href={business.phoneHref}
                    onClick={() => {
                      trackAnalyticsEvent({
                        event: "emergency_cta_click",
                        cta_location: "emergency_hub_hero",
                        lead_type: "emergency",
                      });
                      trackAnalyticsEvent({ event: "phone_click", link_location: "emergency_hub" });
                    }}
                  >
                    <Phone aria-hidden="true" />
                    {common.urgent}
                  </a>
                </Button>
              ) : (
                <Button variant="accent" size="xl" asChild>
                  <a
                    href="#assessment"
                    onClick={() =>
                      trackAnalyticsEvent({
                        event: "estimate_cta_click",
                        cta_location: `${kind}_hero`,
                        lead_type: "planned",
                      })
                    }
                  >
                    {common.estimate}
                    <ArrowRight aria-hidden="true" />
                  </a>
                </Button>
              )}
              <Button variant="outlineDark" size="xl" asChild>
                <a href={isBcHub ? path("/service-areas") : path("/services")}>
                  {isBcHub ? (ui?.common.serviceAreas ?? common.services) : common.explore}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-background">
        <div className="container-page grid gap-px bg-border md:grid-cols-[0.8fr_1.1fr_1.1fr]">
          <div className="bg-slate-deep p-7 text-primary-foreground md:p-9">
            <p className="eyebrow">{common.availability}</p>
            <p className="mt-3 text-sm leading-6 text-primary-foreground/70">
              {isEmergency ? common.emergency : common.planned}
            </p>
          </div>
          <div className="bg-background p-7 md:p-9">
            <p className="text-sm leading-7 text-muted-foreground">
              {isBcHub ? common.emergency : common.mobilization}
            </p>
          </div>
          <div className="bg-background p-7 md:p-9">
            <p className="text-sm leading-7 text-muted-foreground">
              {isEmergency ? common.mobilization : copy.focus[0]}
            </p>
          </div>
        </div>
      </section>

      <section className="section-y">
        <div className="container-page grid gap-14 lg:grid-cols-[0.78fr_1.22fr] lg:gap-20">
          <div>
            <p className="eyebrow">{copy.sectionEyebrow}</p>
            <h2 className="display-2 mt-4 text-balance">{copy.sectionTitle}</h2>
            <p className="mt-5 max-w-xl leading-7 text-muted-foreground">{copy.sectionBody}</p>
            {copy.detailParagraphs?.length ? (
              <div className="mt-8 max-w-xl space-y-5 text-sm leading-7 text-muted-foreground">
                {copy.detailParagraphs.map((paragraph) => (
                  <p key={paragraph.slice(0, 48)}>{paragraph}</p>
                ))}
              </div>
            ) : null}

            <div className="mt-10 border-y border-border">
              <p className="py-4 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                {common.focus}
              </p>
              {copy.focus.map((item, index) => (
                <div key={item} className="flex items-center gap-4 border-t border-border py-4">
                  <span className="font-display text-2xl text-accent">0{index + 1}</span>
                  <span className="text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="eyebrow">{common.related}</p>
            <p className="mt-3 text-sm leading-7 text-muted-foreground">{common.relatedBody}</p>
            <div className="mt-7 grid gap-4 sm:grid-cols-2">
              {services.map((service) => (
                <a
                  key={service.slug}
                  href={path(service.path)}
                  className="group flex min-h-48 flex-col justify-between rounded-xl border border-border bg-card p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-accent/55 hover:shadow-card"
                >
                  <div>
                    <CheckCircle2 className="size-5 text-accent" aria-hidden="true" />
                    <h3 className="mt-5 text-xl leading-tight">
                      {service.shortTitle ?? service.title}
                    </h3>
                    <p className="mt-3 line-clamp-3 text-sm leading-6 text-muted-foreground">
                      {service.description}
                    </p>
                  </div>
                  <span className="mt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.13em] text-accent">
                    {common.learn}
                    <ArrowRight
                      className="transition-transform group-hover:translate-x-1"
                      aria-hidden="true"
                    />
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {isBcHub ? (
        <section className="border-y border-border bg-muted/55 py-16 md:py-20">
          <div className="container-page">
            <div className="max-w-2xl">
              <p className="eyebrow">{common.regions}</p>
              <p className="mt-4 text-sm leading-7 text-muted-foreground">{common.regionNote}</p>
            </div>
            <div className="mt-9 grid gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
              {bcCoverageRegions.map((region) => (
                <div key={region.id} className="flex items-center gap-3 bg-background p-5">
                  <MapPinned className="size-4 shrink-0 text-accent" aria-hidden="true" />
                  <span className="text-sm font-medium">
                    {localizedRegionNames[locale]?.[region.id] ?? region.name}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-10 max-w-3xl border-t border-border pt-8">
              <p className="eyebrow">{common.citiesTitle}</p>
              <p className="mt-4 text-sm leading-7 text-muted-foreground">{common.citiesBody}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {confirmedServiceAreas.slice(0, 5).map((area) => (
                  <a
                    key={area.slug}
                    href={path(`/service-areas/${area.slug}`)}
                    className="rounded-full border border-border bg-background px-3 py-1.5 text-sm font-medium underline-offset-4 hover:border-accent hover:underline"
                  >
                    {area.name}
                  </a>
                ))}
                <a
                  href={path("/service-areas")}
                  className="rounded-full border border-foreground px-3 py-1.5 text-sm font-semibold underline-offset-4 hover:bg-foreground hover:text-background"
                >
                  {common.citiesIndex}
                </a>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <section id="assessment" className="section-y scroll-mt-24 bg-slate-deep">
        <div className="container-page grid gap-12 lg:grid-cols-[0.78fr_1.22fr] lg:gap-16">
          <div className="text-primary-foreground">
            <p className="eyebrow">{common.estimate}</p>
            <h2 className="display-2 mt-4 text-balance">{common.formTitle}</h2>
            <p className="mt-5 max-w-md leading-7 text-primary-foreground/70">{common.formBody}</p>
            {isEmergency ? (
              <Button variant="outlineDark" size="xl" asChild className="mt-8">
                <a
                  href={business.phoneHref}
                  onClick={() => {
                    trackAnalyticsEvent({
                      event: "emergency_cta_click",
                      cta_location: "emergency_hub_form_intro",
                      lead_type: "emergency",
                    });
                    trackAnalyticsEvent({
                      event: "phone_click",
                      link_location: "emergency_hub_form",
                    });
                  }}
                >
                  <Phone aria-hidden="true" />
                  {business.phone}
                </a>
              </Button>
            ) : null}
          </div>
          {isEmergency ? (
            <EmergencyIntakeForm />
          ) : kind === "roof-estimate" ? (
            <PlannedEstimateForm />
          ) : (
            <QuoteForm />
          )}
        </div>
      </section>
    </>
  );
}
